import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, 
  ResponsiveContainer, Legend, RadialBarChart, RadialBar,
  AreaChart, Area, CartesianGrid
} from 'recharts';
import {
  Download, FileText, CheckCircle, XCircle, AlertCircle, 
  MinusCircle, TrendingUp, Shield, ClipboardCheck, FileDown,
  BarChart3, PieChart as PieChartIcon, Activity, AlertTriangle
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Professional color palette
const COLORS = {
  compliant: '#10B981',      // Green
  partial: '#F59E0B',        // Amber
  nonCompliant: '#EF4444',   // Red
  notApplicable: '#6B7280',  // Gray
  noResponse: '#CBD5E1',     // Light gray
  primary: '#6366F1',        // Indigo
  secondary: '#8B5CF6',      // Purple
  background: '#F8FAFC',     // Light background
  card: '#FFFFFF',           // White cards
  text: '#1E293B',           // Dark text
  textSecondary: '#64748B',  // Secondary text
  border: '#E2E8F0'          // Light border
};

const CHART_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#6B7280', '#CBD5E1'];

const SspDashboard = ({ complianceScore, detailedControls, evidenceChecklist, summary, assessmentId }) => {
  
  // Calculate response breakdown data - must be called before early returns
  const responseBreakdown = useMemo(() => {
    if (!complianceScore) return [];
    return [
      { name: 'Compliant (Yes)', value: complianceScore.summary?.yes || 0, color: COLORS.compliant },
      { name: 'Partial', value: complianceScore.summary?.partial || 0, color: COLORS.partial },
      { name: 'Non-Compliant (No)', value: complianceScore.summary?.no || 0, color: COLORS.nonCompliant },
      { name: 'Not Applicable', value: complianceScore.summary?.na || 0, color: COLORS.notApplicable },
      { name: 'No Response', value: complianceScore.summary?.noResponse || 0, color: COLORS.noResponse }
    ].filter(item => item.value > 0);
  }, [complianceScore]);

  // Calculate section/family scores for bar chart
  const familyScores = useMemo(() => {
    if (!complianceScore?.familyScores || !Array.isArray(complianceScore.familyScores)) return [];
    return complianceScore.familyScores
      .filter(f => f && f.family)
      .map(f => ({
        name: (f.family?.length || 0) > 20 ? f.family.substring(0, 20) + '...' : (f.family || 'Unknown'),
        fullName: f.family || 'Unknown',
        compliant: f.compliant || 0,
        partial: f.partial || 0,
        nonCompliant: f.nonCompliant || 0,
        percentage: f.percentage || 0,
        total: f.total || 0
      }));
  }, [complianceScore]);

  // Calculate risk areas (controls with No or Partial answers)
  const riskAreas = useMemo(() => {
    if (!detailedControls?.categories) return [];
    const risks = [];
    detailedControls.categories.forEach(category => {
      category.questions?.forEach(q => {
        if (q.answer === 'N' || q.answer === 'P') {
          risks.push({
            id: q.id,
            question: q.question,
            category: category.name,
            answer: q.answer,
            priority: q.answer === 'N' ? 'High' : 'Medium'
          });
        }
      });
    });
    return risks.slice(0, 20); // Top 20 risk areas
  }, [detailedControls]);

  // Evidence collection status
  const evidenceStatus = useMemo(() => {
    if (!evidenceChecklist) return { collected: 0, total: 0, percentage: 0 };
    const total = evidenceChecklist.totalItems || 0;
    const collected = evidenceChecklist.collectedItems || 0;
    return {
      collected,
      total,
      percentage: total > 0 ? Math.round((collected / total) * 100) : 0
    };
  }, [evidenceChecklist]);

  // Evidence by category for chart
  const evidenceByCategory = useMemo(() => {
    if (!evidenceChecklist?.categories) return [];
    return Object.entries(evidenceChecklist.categories)
      .filter(([name, items]) => Array.isArray(items))
      .map(([name, items]) => ({
        name,
        total: items?.length || 0,
        collected: items?.filter(i => i?.collected)?.length || 0
      }));
  }, [evidenceChecklist]);

  // MUST statements completion
  const mustStatementsData = useMemo(() => {
    if (!detailedControls?.categories) return { completed: 0, total: 0, percentage: 0 };
    let completed = 0;
    let total = 0;
    detailedControls.categories.forEach(category => {
      category.questions?.forEach(q => {
        if (q.mustStatements && Array.isArray(q.mustStatements)) {
          total += q.mustStatements.length;
          completed += q.mustStatements.filter(m => m?.completed).length;
        }
      });
    });
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [detailedControls]);

  // Early return if no data is available yet (must be after all hooks)
  if (!complianceScore && !detailedControls && !evidenceChecklist) {
    return (
      <div className="p-8 text-center text-slate-400">
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  // Generate PDF Report
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFillColor(99, 102, 241);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('SSP Compliance Dashboard', pageWidth / 2, 25, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 35, { align: 'center' });
    
    let yPos = 55;
    
    // Executive Summary
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Executive Summary', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    
    const summaryData = [
      ['Overall Compliance Score', `${complianceScore?.overallScore?.percentage || 0}%`],
      ['Total Controls', complianceScore?.summary?.total?.toString() || '0'],
      ['Compliant Controls', complianceScore?.summary?.yes?.toString() || '0'],
      ['Partial Compliance', complianceScore?.summary?.partial?.toString() || '0'],
      ['Non-Compliant Controls', complianceScore?.summary?.no?.toString() || '0'],
      ['Evidence Collected', `${evidenceStatus.collected} / ${evidenceStatus.total}`],
      ['MUST Statements Complete', `${mustStatementsData.completed} / ${mustStatementsData.total}`]
    ];
    
    autoTable(doc, {
      startY: yPos,
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [99, 102, 241] },
      bodyStyles: { textColor: [30, 41, 59] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
    
    // Response Breakdown
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Response Breakdown', 20, yPos);
    yPos += 10;
    
    const responseData = responseBreakdown.map(r => [r.name, r.value.toString()]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Response Type', 'Count']],
      body: responseData,
      theme: 'striped',
      headStyles: { fillColor: [99, 102, 241] }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
    
    // Compliance by Section
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Compliance by Control Family', 20, yPos);
    yPos += 10;
    
    const familyData = familyScores.map(f => [
      f.fullName,
      f.compliant.toString(),
      f.partial.toString(),
      f.nonCompliant.toString(),
      `${f.percentage}%`
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Control Family', 'Compliant', 'Partial', 'Non-Compliant', 'Score']],
      body: familyData,
      theme: 'striped',
      headStyles: { fillColor: [99, 102, 241] },
      columnStyles: { 4: { halign: 'center' } }
    });
    
    // Risk Areas
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('High Priority Risk Areas', 20, yPos);
    yPos += 10;
    
    const riskData = riskAreas.slice(0, 15).map(r => [
      r.id,
      r.category,
      r.answer === 'N' ? 'Non-Compliant' : 'Partial',
      r.priority
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Control ID', 'Category', 'Status', 'Priority']],
      body: riskData,
      theme: 'striped',
      headStyles: { fillColor: [239, 68, 68] },
      didParseCell: function(data) {
        if (data.column.index === 3 && data.section === 'body') {
          if (data.cell.text[0] === 'High') {
            data.cell.styles.textColor = [239, 68, 68];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });
    
    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `SSP Compliance Dashboard - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`SSP-Dashboard-${assessmentId || 'report'}.pdf`);
  };

  // Generate CSV Export
  const generateCSV = () => {
    const rows = [];
    
    // Header
    rows.push(['SSP Compliance Dashboard Export']);
    rows.push([`Generated: ${new Date().toISOString()}`]);
    rows.push([`Assessment ID: ${assessmentId || 'N/A'}`]);
    rows.push([]);
    
    // Summary Section
    rows.push(['=== EXECUTIVE SUMMARY ===']);
    rows.push(['Metric', 'Value']);
    rows.push(['Overall Compliance Score', `${complianceScore?.overallScore?.percentage || 0}%`]);
    rows.push(['Total Controls', complianceScore?.summary?.total || 0]);
    rows.push(['Compliant (Yes)', complianceScore?.summary?.yes || 0]);
    rows.push(['Partial', complianceScore?.summary?.partial || 0]);
    rows.push(['Non-Compliant (No)', complianceScore?.summary?.no || 0]);
    rows.push(['Not Applicable', complianceScore?.summary?.na || 0]);
    rows.push(['No Response', complianceScore?.summary?.noResponse || 0]);
    rows.push([]);
    
    // Detailed Responses Section
    rows.push(['=== DETAILED QUESTION RESPONSES ===']);
    rows.push(['Question ID', 'Category', 'Question', 'Answer', 'Response Text', 'MUST Statements Completed']);
    
    if (detailedControls?.categories) {
      detailedControls.categories.forEach(category => {
        category.questions?.forEach(q => {
          const mustCompleted = q.mustStatements && Array.isArray(q.mustStatements)
            ? `${q.mustStatements.filter(m => m?.completed).length}/${q.mustStatements.length}`
            : 'N/A';
          rows.push([
            q.id,
            category.name,
            `"${(q.question || '').replace(/"/g, '""')}"`,
            q.answer || 'No Response',
            `"${(q.responseText || '').replace(/"/g, '""')}"`,
            mustCompleted
          ]);
        });
      });
    }
    
    rows.push([]);
    
    // Family Scores Section
    rows.push(['=== COMPLIANCE BY CONTROL FAMILY ===']);
    rows.push(['Control Family', 'Compliant', 'Partial', 'Non-Compliant', 'Total', 'Percentage']);
    
    familyScores.forEach(f => {
      rows.push([f.fullName, f.compliant, f.partial, f.nonCompliant, f.total, `${f.percentage}%`]);
    });
    
    rows.push([]);
    
    // Risk Areas
    rows.push(['=== RISK AREAS (Non-Compliant & Partial) ===']);
    rows.push(['Control ID', 'Category', 'Question', 'Status', 'Priority']);
    
    riskAreas.forEach(r => {
      rows.push([
        r.id,
        r.category,
        `"${(r.question || '').replace(/"/g, '""')}"`,
        r.answer === 'N' ? 'Non-Compliant' : 'Partial',
        r.priority
      ]);
    });
    
    // Convert to CSV string
    const csvContent = rows.map(row => row.join(',')).join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `SSP-Dashboard-${assessmentId || 'report'}.csv`;
    link.click();
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800">{label || payload[0].name}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-slate-100 rounded-2xl p-6" data-testid="ssp-dashboard">
      {/* Dashboard Header */}
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-indigo-600" />
              Compliance Dashboard
            </h2>
            <p className="text-gray-500 mt-1">
              Assessment overview and analytics • {new Date().toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={generatePDF}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm"
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4" />
              Export PDF
            </button>
            <button
              onClick={generateCSV}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-sm"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Overall Score Card */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Overall Score</span>
            <Activity className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="flex items-end gap-2">
            <span className={`text-4xl font-bold ${
              (complianceScore?.overallScore?.percentage || 0) >= 70 ? 'text-emerald-600' :
              (complianceScore?.overallScore?.percentage || 0) >= 40 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {complianceScore?.overallScore?.percentage || 0}%
            </span>
          </div>
          <p className="text-gray-400 text-xs mt-2">
            {complianceScore?.summary?.yes || 0} of {complianceScore?.summary?.total || 0} controls compliant
          </p>
        </div>

        {/* Compliant Controls */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Compliant Controls</span>
            <CheckCircle className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-emerald-600">
              {complianceScore?.summary?.yes || 0}
            </span>
            <span className="text-gray-400 text-sm mb-1">/ {complianceScore?.summary?.total || 0}</span>
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${((complianceScore?.summary?.yes || 0) / (complianceScore?.summary?.total || 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Evidence Status */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Evidence Collected</span>
            <ClipboardCheck className="w-5 h-5 text-blue-500" />
          </div>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-blue-600">
              {evidenceStatus.percentage}%
            </span>
          </div>
          <p className="text-gray-400 text-xs mt-2">
            {evidenceStatus.collected} of {evidenceStatus.total} items collected
          </p>
        </div>

        {/* Risk Items */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Risk Areas</span>
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-red-600">
              {riskAreas.length}
            </span>
            <span className="text-gray-400 text-sm mb-1">items need attention</span>
          </div>
          <p className="text-gray-400 text-xs mt-2">
            {riskAreas.filter(r => r.priority === 'High').length} high priority
          </p>
        </div>
      </div>

      {/* Charts Row 1 - Response Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Response Breakdown Pie Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-indigo-500" />
            Response Breakdown
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={responseBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {responseBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => <span className="text-gray-600 text-sm">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Response Summary Table */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-500" />
            Response Summary
          </h3>
          <div className="space-y-3">
            {responseBreakdown.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-gray-700 font-medium">{item.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-800 font-bold text-lg">{item.value}</span>
                  <span className="text-gray-400 text-sm w-16 text-right">
                    {((item.value / (complianceScore?.summary?.total || 1)) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 - Section Progress */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-500" />
          Compliance by Control Family
        </h3>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={familyScores} layout="vertical" margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" domain={[0, 'dataMax']} stroke="#64748B" />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={150} 
                tick={{ fontSize: 12, fill: '#64748B' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="compliant" name="Compliant" stackId="a" fill={COLORS.compliant} />
              <Bar dataKey="partial" name="Partial" stackId="a" fill={COLORS.partial} />
              <Bar dataKey="nonCompliant" name="Non-Compliant" stackId="a" fill={COLORS.nonCompliant} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 3 - Evidence & MUST Statements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Evidence by Category */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-indigo-500" />
            Evidence Collection by Category
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={evidenceByCategory} margin={{ bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={60}
                  tick={{ fontSize: 11, fill: '#64748B' }}
                />
                <YAxis stroke="#64748B" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="collected" name="Collected" fill={COLORS.compliant} />
                <Bar dataKey="total" name="Required" fill={COLORS.border} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* MUST Statements Progress */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-500" />
            MUST Statements Completion
          </h3>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="relative w-40 h-40 mx-auto">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#E2E8F0"
                    strokeWidth="10"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke={COLORS.primary}
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${mustStatementsData.percentage * 2.83} 283`}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-gray-800">
                    {mustStatementsData.percentage}%
                  </span>
                </div>
              </div>
              <p className="text-gray-500 mt-4">
                {mustStatementsData.completed} of {mustStatementsData.total} statements completed
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Areas Table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          Priority Risk Areas
          <span className="text-sm font-normal text-gray-400 ml-2">
            (Non-compliant and Partial controls requiring attention)
          </span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="risk-areas-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-3 px-4 text-gray-600 font-semibold text-sm">Control ID</th>
                <th className="text-left py-3 px-4 text-gray-600 font-semibold text-sm">Category</th>
                <th className="text-left py-3 px-4 text-gray-600 font-semibold text-sm">Status</th>
                <th className="text-left py-3 px-4 text-gray-600 font-semibold text-sm">Priority</th>
              </tr>
            </thead>
            <tbody>
              {riskAreas.slice(0, 10).map((risk, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono text-sm text-indigo-600">{risk.id}</td>
                  <td className="py-3 px-4 text-gray-700 text-sm">{risk.category}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      risk.answer === 'N' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {risk.answer === 'N' ? (
                        <><XCircle className="w-3 h-3" /> Non-Compliant</>
                      ) : (
                        <><AlertCircle className="w-3 h-3" /> Partial</>
                      )}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      risk.priority === 'High' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {risk.priority}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {riskAreas.length > 10 && (
            <p className="text-center text-gray-400 text-sm mt-4">
              Showing 10 of {riskAreas.length} risk areas. Export CSV for complete list.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SspDashboard;
